import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { Table, Badge, Button, Form, InputGroup } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  GET_CARRIER_IMPORT_BATCHES,
  GET_CARRIER_IMPORT_BATCHES_COUNT,
} from '../../../gql/queries/carrierImportQueries'
import { GET_ALL_CARRIERS_LIST } from '../../../gql/queries/carriersQuery'
import { PaginationSection } from '../../shared/PaginationSection'
import { PaginationUtils } from '../../shared/PaginationUtils'
import moment from 'moment'

// Mirrors ADAPTER_REGISTRY keys + special-cased names from get_adapter_for_carrier()
const SUPPORTED_KEYWORDS = [
  'ambetter', 'oscar', 'bcbs', 'blue cross', 'blue shield of california',
  'molina', 'anthem', 'wellpoint', 'imperial', 'sentara', 'jefferson',
  'independence', 'amerihealth', 'ameri health', 'cigna', 'alliant',
  'highmark', 'medica', 'united healthcare',
]

const isCarrierSupported = (name: string) => {
  const lower = name.toLowerCase()
  if (lower.includes('caritas')) return false
  return SUPPORTED_KEYWORDS.some((kw) => lower.includes(kw))
}

interface CarrierImportBatch {
  id: string
  carrier: { id: string; name: string }
  asOfDate: string
  uploadedBy: { id: string; firstName: string; lastName: string }
  uploadedAt: string
  originalFilename: string
  rowCount: number
  processedCount: number
  matchedCount: number
  unmatchedCount: number
  errorsCount: number
}

interface CarrierInfo { id: string; name: string }

interface CarrierImportHistoryProps {
  onViewDetails: (batchId: string) => void
  onOpenCarrierImport?: () => void
}

const COLS = 9

const SkeletonRows: React.FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <tr key={i} className='placeholder-glow'>
        <td><span className='placeholder rounded' style={{ width: 110, display: 'inline-block' }} /></td>
        <td><span className='placeholder rounded' style={{ width: 90, display: 'inline-block' }} /></td>
        <td><span className='placeholder rounded' style={{ width: 80, display: 'inline-block' }} /></td>
        <td><span className='placeholder rounded' style={{ width: 100, display: 'inline-block' }} /></td>
        {Array.from({ length: 5 }).map((__, j) => (
          <td key={j} className='text-center'>
            <span className='placeholder rounded' style={{ width: 36, display: 'inline-block' }} />
          </td>
        ))}
      </tr>
    ))}
  </>
)

export const CarrierImportHistory: React.FC<CarrierImportHistoryProps> = ({
  onViewDetails,
  onOpenCarrierImport,
}) => {
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>('')
  const [dateFromObj, setDateFromObj] = useState<Date | null>(null)
  const [dateToObj, setDateToObj] = useState<Date | null>(null)

  const offset = (currentPage - 1) * pageSize
  const variables = {
    carrierId: selectedCarrierId || undefined,
    dateFrom: dateFromObj ? moment(dateFromObj).format('YYYY-MM-DD') : undefined,
    dateTo: dateToObj ? moment(dateToObj).format('YYYY-MM-DD') : undefined,
  }

  const { data: carriersData } = useQuery<{ allInsuranceOperatorsList: CarrierInfo[] }>(
    GET_ALL_CARRIERS_LIST
  )

  const { data: countData } = useQuery<{ carrierImportBatchesCount: number }>(
    GET_CARRIER_IMPORT_BATCHES_COUNT,
    { variables, fetchPolicy: 'network-only' }
  )

  const { data, loading, error, refetch } = useQuery(GET_CARRIER_IMPORT_BATCHES, {
    variables: { ...variables, limit: pageSize, offset, orderBy: '-uploaded_at' },
    fetchPolicy: 'network-only',
  })

  const batches: CarrierImportBatch[] = data?.carrierImportBatches || []
  const totalItems = countData?.carrierImportBatchesCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const { start, end } = PaginationUtils.getPaginationRange(currentPage, pageSize, totalItems)

  const supportedCarriers = (carriersData?.allInsuranceOperatorsList || [])
    .filter((c) => isCarrierSupported(c.name))
    .sort((a, b) => a.name.localeCompare(b.name))

  const resetPage = () => setCurrentPage(1)

  return (
    <div className='carrier-import-history'>

      {/* ── Title + action buttons ── */}
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h5 className='mb-0'>Carrier Import History</h5>
        <div className='d-flex gap-2'>
          {onOpenCarrierImport && (
            <Button size='sm' onClick={onOpenCarrierImport}>
              <i className='bi bi-cloud-upload fs-3' /> Carrier Import
            </Button>
          )}
          <Button
            size='sm'
            variant='outline-primary'
            style={{ border: '1px solid #dbdfe9' }}
            onClick={() => refetch()}
            disabled={loading}
          >
            <i className='bi bi-arrow-clockwise' /> Refresh
          </Button>
        </div>
      </div>

      {/* ── Filters bar (same level as PoliciesPage search bar) ── */}
      <div className='filters-header'>
        <div className='line1'>
          <InputGroup>
            <Form.Select
              value={selectedCarrierId}
              onChange={(e) => { setSelectedCarrierId(e.target.value); resetPage() }}
              style={{ maxWidth: 300 }}
            >
              <option value=''>All carriers</option>
              {supportedCarriers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Form.Select>
            <DatePicker
              selected={dateFromObj}
              onChange={(date) => { setDateFromObj(date); resetPage() }}
              dateFormat='MM/dd/yyyy'
              placeholderText='From MM/DD/YYYY'
              showMonthDropdown
              showYearDropdown
              dropdownMode='select'
              isClearable
              customInput={
                <Form.Control style={{ maxWidth: 170 }} />
              }
            />
            <DatePicker
              selected={dateToObj}
              onChange={(date) => { setDateToObj(date); resetPage() }}
              dateFormat='MM/dd/yyyy'
              placeholderText='To MM/DD/YYYY'
              showMonthDropdown
              showYearDropdown
              dropdownMode='select'
              isClearable
              customInput={
                <Form.Control style={{ maxWidth: 170 }} />
              }
            />
            {(selectedCarrierId || dateFromObj || dateToObj) && (
              <Button
                variant='outline-secondary'
                style={{ border: '1px solid #dbdfe9' }}
                onClick={() => {
                  setSelectedCarrierId('')
                  setDateFromObj(null)
                  setDateToObj(null)
                  resetPage()
                }}
                title='Clear filters'
              >
                <i className='bi bi-x-lg' />
              </Button>
            )}
          </InputGroup>
        </div>
      </div>

      {error && (
        <div className='alert alert-danger py-2 mt-2'>Error: {error.message}</div>
      )}

      {/* ── Pagination controls ── */}
      <div className='results-header'>
        <PaginationSection
          first={pageSize}
          setFirst={(n) => { setPageSize(n); resetPage() }}
          currentPage={currentPage}
          totalPages={totalPages}
          start={start}
          end={end}
          totalPolicies={totalItems}
          updatePage={setCurrentPage}
          PaginationUtils={PaginationUtils}
        />
        <div className='col-md-3'>
          <div className='results-filter' />
        </div>
      </div>

      {/* ── Table ── */}
      <div className='table-responsive table table-striped table-hover'>
        <Table striped hover className='mb-0'>
          <thead>
            <tr>
              <th>Uploaded</th>
              <th>Carrier</th>
              <th>As-of Date</th>
              <th>Uploaded By</th>
              <th className='text-center'>Total</th>
              <th className='text-center'>Matched</th>
              <th className='text-center'>Unmatched</th>
              <th className='text-center'>Errors</th>
              <th className='text-center'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows count={pageSize} />
            ) : batches.length === 0 ? (
              <tr>
                <td colSpan={COLS} className='text-center text-muted py-4'>
                  No import history found
                </td>
              </tr>
            ) : (
              batches.map((batch) => (
                <tr key={batch.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {moment(batch.uploadedAt).format('MM/DD/YYYY hh:mm A')}
                  </td>
                  <td>{batch.carrier.name}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{batch.asOfDate || 'N/A'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {batch.uploadedBy.firstName} {batch.uploadedBy.lastName}
                  </td>
                  <td className='text-center'>
                    <Badge bg='secondary'>{batch.processedCount}</Badge>
                  </td>
                  <td className='text-center'>
                    <Badge bg='success'>{batch.matchedCount}</Badge>
                  </td>
                  <td className='text-center'>
                    <Badge bg='warning' text='dark'>{batch.unmatchedCount}</Badge>
                  </td>
                  <td className='text-center'>
                    <Badge bg='danger'>{batch.errorsCount}</Badge>
                  </td>
                  <td className='text-center' style={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size='sm'
                      variant='outline-primary'
                      onClick={() => onViewDetails(batch.id)}
                      disabled={batch.unmatchedCount === 0}
                    >
                      <i className='bi bi-eye' /> View Details
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  )
}
